import React, { useState } from 'react';
import { changePassword, changeEmail } from 'services/employeePortal';

const INPUT = {
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: '10px 40px 10px 14px',
    width: '100%',
    fontSize: 14,
    outline: 'none',
    background: '#fff',
};

const CARD = {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    padding: '24px 28px',
    marginBottom: 24,
};

function PasswordInput({ label, value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#374151' }}>{label}</label>
            <div style={{ position: 'relative' }}>
                <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    style={INPUT}
                />
                <span
                    onClick={() => setShow(s => !s)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: 16 }}
                >
                    {show ? '🔓' : '🔒'}
                </span>
            </div>
        </div>
    );
}

function Alert({ msg }) {
    if (!msg) return null;
    const isError = msg.type === 'error';
    return (
        <div style={{
            padding: '10px 16px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            fontWeight: 500,
            background: isError ? '#fef2f2' : '#f0fdf4',
            color: isError ? '#dc2626' : '#16a34a',
            border: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
        }}>
            {msg.text}
        </div>
    );
}

export default function AccountSettings() {
    const currentEmail = localStorage.getItem('userEmail') || '';

    // Password change state
    const [oldPw, setOldPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwMsg, setPwMsg] = useState(null);
    const [pwLoading, setPwLoading] = useState(false);

    // Email change state
    const [newEmail, setNewEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [emailMsg, setEmailMsg] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwMsg(null);
        if (newPw.length < 8) {
            setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
            return;
        }
        if (newPw !== confirmPw) {
            setPwMsg({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        setPwLoading(true);
        try {
            await changePassword({ email: currentEmail, oldPassword: oldPw, newPassword: newPw });
            setPwMsg({ type: 'success', text: 'Password updated successfully.' });
            setOldPw(''); setNewPw(''); setConfirmPw('');
        } catch (err) {
            setPwMsg({ type: 'error', text: err?.response?.data?.message || 'Current password is incorrect.' });
        } finally {
            setPwLoading(false);
        }
    };

    const handleChangeEmail = async (e) => {
        e.preventDefault();
        setEmailMsg(null);
        if (newEmail !== confirmEmail) {
            setEmailMsg({ type: 'error', text: 'Email addresses do not match.' });
            return;
        }
        if (!newEmail.includes('@')) {
            setEmailMsg({ type: 'error', text: 'Please enter a valid email address.' });
            return;
        }
        setEmailLoading(true);
        try {
            await changeEmail({ currentEmail, newEmail });
            localStorage.setItem('userEmail', newEmail);
            setEmailMsg({ type: 'success', text: 'Email updated successfully.' });
            setNewEmail(''); setConfirmEmail('');
        } catch (err) {
            setEmailMsg({ type: 'error', text: err?.response?.data?.message || 'Could not update email. Please try again.' });
        } finally {
            setEmailLoading(false);
        }
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a3a5c', fontWeight: 700, marginBottom: 4 }}>Account Settings</h4>
            <p style={{ color: '#6c757d', marginBottom: 28, fontSize: 14 }}>
                Manage your account credentials. Changes apply to your admin login.
            </p>

            {/* Current Account Info */}
            <div style={CARD}>
                <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Account Info
                </h6>
                <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: 4 }}>Current Email</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1a3a5c' }}>{currentEmail}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: 4 }}>Role</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#E7730A' }}>Company Admin</div>
                    </div>
                </div>
            </div>

            {/* Change Password */}
            <div style={CARD}>
                <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Change Password
                </h6>
                <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
                    Choose a strong password with at least 8 characters.
                </p>
                <Alert msg={pwMsg} />
                <form onSubmit={handleChangePassword}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
                        <PasswordInput label="Current Password" value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="Current password" />
                        <PasswordInput label="New Password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters" />
                        <PasswordInput label="Confirm New Password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
                    </div>
                    <button
                        type="submit"
                        disabled={pwLoading || !oldPw || !newPw || !confirmPw}
                        style={{
                            background: pwLoading ? '#ccc' : '#E7730A',
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: pwLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {pwLoading ? 'Updating…' : 'Update Password'}
                    </button>
                </form>
            </div>

            {/* Change Email */}
            <div style={CARD}>
                <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Change Email
                </h6>
                <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
                    Update the email address used to log in. You'll need to confirm your password.
                </p>
                <Alert msg={emailMsg} />
                <form onSubmit={handleChangeEmail}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#374151' }}>New Email</label>
                            <input
                                type="email"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                placeholder="new@email.com"
                                style={INPUT}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#374151' }}>Confirm New Email</label>
                            <input
                                type="email"
                                value={confirmEmail}
                                onChange={e => setConfirmEmail(e.target.value)}
                                placeholder="Repeat new email"
                                style={INPUT}
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={emailLoading || !newEmail || !confirmEmail}
                        style={{
                            background: emailLoading ? '#ccc' : '#E7730A',
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: emailLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {emailLoading ? 'Updating…' : 'Update Email'}
                    </button>
                </form>
            </div>
        </div>
    );
}
